import React, { FC, useMemo, useState } from 'react';
import styles from './selectNewOrganisationModal.module.scss';
import { Col, Form, Modal, Row } from 'antd';
import COButton from 'shared/components/COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { Formik, useFormikContext } from 'formik';
import COSelect from 'shared/components/COSelect';
import { Organization } from 'models/CompanySetup/organization.model';
import { ReactComponent as CompanyIcon } from 'assets/icons/Company_outline.svg';
import { NewNodeType } from 'enums/organisationMapEnums';
import { filterOrganisationList } from './filterOrganisationList.util';
import { MetaData } from 'models/MetaData/metaData.model';
import NewOrganisationModal from './NewOrganisationModal';
import { OrganisationMapPayload } from 'models/CompanySetup/organisationPayload.model';
import { selectOrgValidationSchema } from './newOrganisationValidationSchema';
import {
  generateNewOrganisationData,
  handleAddChild,
  handleAddParent,
} from '../editOrganisationMap.util';
import { modalCloseIcon } from 'shared/components/OrganizationModal';

interface SelectOrganisationProps {
  isOpen: boolean;
  onClose: () => void;
  currentOrganisation: Organization;
  organisationListMeta: MetaData[];
  organisationList: Organization[];
  additionType?: NewNodeType;
  setOrganisationMapData?: React.Dispatch<
    React.SetStateAction<OrganisationMapPayload>
  >;
}

enum SelectOrganisationModalEnum {
  CLOSE_TEXT = 'Go back',
  PROCEED_TEXT = 'Proceed',
  ADD_MAINPARENT_TEXT = 'Add a parent organization above',
  ADD_PARENT_TEXT = 'Add an organization above',
  ADD_CHILD_TEXT = 'Add an organization under',
  DROPDOWN_FIELD_NAME = 'organisation',
  DROPDOWN_FIELD_PLACEHOLDER = 'Choose from the list of organization',
  ADD_ORGANIZATION_TEXT = 'Add organization',
}

const SelectOrganisationModal: FC<SelectOrganisationProps> = ({
  isOpen,
  onClose,
  currentOrganisation,
  organisationList,
  organisationListMeta,
  additionType,
  setOrganisationMapData,
}) => {
  const organisationOptions = useMemo(() => {
    return filterOrganisationList(
      currentOrganisation,
      organisationListMeta,
      organisationList,
      additionType
    );
  }, [organisationList, additionType, currentOrganisation]);

  const [visible, setVisible] = useState<boolean>(false);
  const isParent = additionType === NewNodeType.PARENT;
  const { setFieldValue } = useFormikContext<OrganisationMapPayload>();
  const updateOrganisation = (props: {
    organisationName?: string;
    organisation?: Organization;
  }) => {
    const { organisation, organisationName } = props;
    const selectedOrg =
      (organisationName &&
        organisationList?.find(item => item.name === organisationName)) ||
      organisation ||
      generateNewOrganisationData(organisationListMeta, organisationName ?? '');
    if (!selectedOrg && !organisation) return;
    const updatedOrganisationList: Organization[] = isParent
      ? handleAddParent(
          organisationList,
          currentOrganisation,
          selectedOrg as Organization
        )
      : handleAddChild(
          organisationList,
          currentOrganisation,
          selectedOrg as Organization
        );

    if (!updatedOrganisationList.length || !setOrganisationMapData) return;
    setOrganisationMapData(prev => {
      return { ...prev, organizationMap: updatedOrganisationList };
    });
    setFieldValue('organizationMap', updatedOrganisationList);
    onClose();
  };

  const onSubmit = (values: { organisation?: string }) =>
    updateOrganisation({ organisationName: values.organisation });

  const footer = (submitForm: () => void) => {
    return (
      <Row
        gutter={[15, 15]}
        className={styles.validationFooter}
        justify={'center'}>
        <Col span={12}>
          <COButton primary size={ButtonSize.LARGE} submit onClick={submitForm}>
            {SelectOrganisationModalEnum.PROCEED_TEXT}
          </COButton>
        </Col>
        <Col span={12}>
          <COButton secondary size={ButtonSize.LARGE} onClick={onClose}>
            {SelectOrganisationModalEnum.CLOSE_TEXT}
          </COButton>
        </Col>
      </Row>
    );
  };

  const handleToggleVisible = () => {
    setVisible(prev => !prev);
  };

  return (
    <div>
      <Formik
        initialValues={isOpen ? { organisation: undefined } : {}}
        onSubmit={onSubmit}
        validationSchema={selectOrgValidationSchema}
        enableReinitialize>
        {({ submitForm }) => {
          return (
            <Form>
              <Modal
                destroyOnClose={true}
                maskClosable={false}
                centered
                width={500}
                footer={footer(submitForm)}
                wrapClassName={styles.modalContainer}
                open={isOpen}
                closeIcon={modalCloseIcon}
                onCancel={onClose}>
                <Row className={styles.confirmationContent}>
                  <Col span={24} className={styles.modalTitle}>
                    {`${
                      isParent &&
                      !currentOrganisation?.organisationYearWise?.parentName
                        ? SelectOrganisationModalEnum.ADD_MAINPARENT_TEXT
                        : isParent
                        ? SelectOrganisationModalEnum.ADD_PARENT_TEXT
                        : SelectOrganisationModalEnum.ADD_CHILD_TEXT
                    } ${currentOrganisation.name ?? ''}`}
                  </Col>
                  <Col span={24}>
                    <COSelect
                      name={SelectOrganisationModalEnum.DROPDOWN_FIELD_NAME}
                      placeholderstring={
                        SelectOrganisationModalEnum.DROPDOWN_FIELD_PLACEHOLDER
                      }
                      options={organisationOptions}
                    />
                  </Col>
                  <Col
                    span={12}
                    className={styles.addOrganisation}
                    onClick={handleToggleVisible}>
                    <CompanyIcon />
                    <div>
                      {SelectOrganisationModalEnum.ADD_ORGANIZATION_TEXT}
                    </div>
                  </Col>
                </Row>
              </Modal>
            </Form>
          );
        }}
      </Formik>
      <NewOrganisationModal
        isOpen={visible}
        onClose={handleToggleVisible}
        isMainParent={
          !!(!currentOrganisation.organisationYearWise?.parentName && isParent)
        }
        updateOrganisation={updateOrganisation}
        organisationList={organisationList}
      />
    </div>
  );
};

export default SelectOrganisationModal;
